/**
 * "Hypotézy" above the investigation cards (PRD-0075 R-E1): the case's hypotheses, open ones
 * first, each as a card with its strip. Presentational: the feature
 * (`features/analytic-hypotheses`) reads the list and owns the create dialog.
 */
import { Lightbulb, Plus, RefreshCw } from 'lucide-react'
import { useId } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import type { AnalyticHypothesis } from '@/types/analytic-hypothesis'
import { HypothesisCard } from './HypothesisCard'

const COPY = 'analyticHypotheses.section'

export type HypothesesSectionState =
  | { status: 'loading' }
  | { status: 'failed' }
  | { status: 'ready'; hypotheses: readonly AnalyticHypothesis[] }

export interface HypothesesSectionViewProps {
  view: HypothesesSectionState
  /** The page of one hypothesis. */
  hypothesisHref: (hypothesisId: string) => string
  /** Opens the create dialog; null for a reader. */
  onCreate: (() => void) | null
  onRetry: () => void
}

function Body({ view, hypothesisHref, onRetry }: Omit<HypothesesSectionViewProps, 'onCreate'>) {
  const { t } = useTranslation()
  if (view.status === 'loading') {
    return (
      <div role="status" className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3">
        <span className="sr-only">{t(`${COPY}.loading`)}</span>
        <Skeleton aria-hidden className="h-40 w-full" />
        <Skeleton aria-hidden className="hidden h-40 w-full sm:block" />
      </div>
    )
  }
  if (view.status === 'failed') {
    return (
      <div role="alert" className="flex flex-wrap items-center gap-2 text-sm">
        <p className="text-destructive">{t(`${COPY}.loadFailed`)}</p>
        <Button type="button" variant="outline" size="sm" onClick={onRetry}>
          <RefreshCw aria-hidden />
          {t(`${COPY}.retry`)}
        </Button>
      </div>
    )
  }
  if (view.hypotheses.length === 0) {
    return (
      <p className="rounded-xl border border-dashed border-border/60 px-4 py-6 text-center text-sm text-muted-foreground">
        {t(`${COPY}.empty`)}
      </p>
    )
  }
  return (
    <ul className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3">
      {view.hypotheses.map(hypothesis => (
        <li key={hypothesis.id} className="flex">
          <HypothesisCard hypothesis={hypothesis} to={hypothesisHref(hypothesis.id)} />
        </li>
      ))}
    </ul>
  )
}

export function HypothesesSectionView({ onCreate, ...body }: HypothesesSectionViewProps) {
  const { t } = useTranslation()
  const headingId = useId()
  const count = body.view.status === 'ready' ? body.view.hypotheses.length : null
  return (
    <section aria-labelledby={headingId}>
      <div className="mb-3 flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2
            id={headingId}
            className="flex items-center gap-1.5 text-sm font-semibold text-foreground"
          >
            <Lightbulb aria-hidden className="size-4 text-muted-foreground" />
            {t(`${COPY}.title`)}
          </h2>
          <p className="mt-0.5 text-xs text-muted-foreground">{t(`${COPY}.hint`)}</p>
        </div>
        <div className="flex items-center gap-3">
          {count !== null && (
            <span className="whitespace-nowrap font-mono text-xs text-muted-foreground">
              {t(`${COPY}.count`, { count })}
            </span>
          )}
          {onCreate && (
            <Button type="button" size="sm" onClick={onCreate}>
              <Plus aria-hidden />
              {t(`${COPY}.new`)}
            </Button>
          )}
        </div>
      </div>
      <Body {...body} />
    </section>
  )
}
